import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Skeleton from '@material-ui/lab/Skeleton';
import { Header } from '@backstage/core-components';
import { useApi, configApiRef, useRouteRef } from '@backstage/core-plugin-api';
import { entityPresentationApiRef } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  TechDocsReaderPageContent,
  TechDocsReaderPageHeader,
  TechDocsReaderPageSubheader,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import {
  useTechDocsReaderPage,
  useTechDocsAddons,
  TechDocsAddonLocations,
} from '@backstage/plugin-techdocs-react';
import { useIsGuest } from '../guest';

const skeleton = <Skeleton animation="wave" variant="text" height={40} />;

const toTitle = (segment: string) =>
  segment
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

const GuestTechDocsReaderPageHeader = () => {
  const addons = useTechDocsAddons();
  const configApi = useApi(configApiRef);
  const entityPresentationApi = useApi(entityPresentationApiRef);
  const docsRootLink = useRouteRef(techdocsPlugin.routes.root)();
  const { '*': path = '' } = useParams();
  const {
    title,
    setTitle,
    subtitle,
    setSubtitle,
    entityRef,
    entityMetadata: { value: entityMetadata, loading: entityMetadataLoading },
    metadata: { value: metadata, loading: metadataLoading },
  } = useTechDocsReaderPage();

  const hidden =
    (!entityMetadataLoading && entityMetadata === undefined) ||
    (!metadataLoading && metadata === undefined);

  useEffect(() => {
    if (!metadata) {
      return;
    }
    setTitle(metadata.site_name);
    setSubtitle(
      !metadata.site_description || metadata.site_description === 'None'
        ? ''
        : metadata.site_description,
    );
  }, [metadata, setTitle, setSubtitle]);

  useEffect(() => {
    const appTitle = configApi.getOptionalString('app.title') || 'Backstage';
    if (hidden) {
      document.title = appTitle;
      return;
    }
    const entityTitle = entityPresentationApi.forEntity(
      stringifyEntityRef(entityRef),
    ).snapshot.primaryTitle;
    const pathItems = path
      ? path.replace(/\/$/, '').split('/').map(toTitle)
      : [];
    document.title = [entityTitle, ...pathItems, appTitle].join(' | ');
  }, [configApi, entityPresentationApi, entityRef, path, hidden]);

  if (hidden) {
    return null;
  }

  return (
    <Header
      type="Documentation"
      typeLink={docsRootLink}
      title={title || skeleton}
      subtitle={subtitle === '' ? undefined : subtitle || skeleton}
    >
      {addons.renderComponentsByLocation(TechDocsAddonLocations.Header)}
    </Header>
  );
};

const GuestAwareTechDocsReaderPageBody = () => {
  const isGuest = useIsGuest();

  return (
    <>
      {isGuest ? (
        <GuestTechDocsReaderPageHeader />
      ) : (
        <TechDocsReaderPageHeader />
      )}
      <TechDocsReaderPageSubheader />
      <TechDocsReaderPageContent />
    </>
  );
};

export const renderGuestAwareTechDocsReaderPage = () => (
  <GuestAwareTechDocsReaderPageBody />
);
